import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, IconButton, Typography, Alert } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import { enUS } from '@mui/material/locale';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';
import Sidebar from './pages/Sidebar';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Collections from './Collections';
import Jobs from './pages/Jobs';
import Drivers from './pages/Drivers';
import DriverDetail from './pages/DriverDetail';
import Expenses from './pages/Expenses';
import Invoices from './pages/Invoices';
import Reports from './pages/Reports';
import DriverPortal from './pages/DriverPortal';
import Calendar from './pages/Calendar';
import Users from './pages/Users';
import Settings from './pages/Settings';
import AuditLog from './pages/AuditLog';
import CommunicationLog from './pages/CommunicationLog';
import GoogleAdsExports from './pages/GoogleAdsExports';
import Login from './pages/Login';
import Register from './Register';
import SOPLibrary from './pages/SOPLibrary';
import Estimates from './pages/Estimates';
import PublicEstimate from './pages/PublicEstimate';
import PublicBooking from './pages/PublicBooking';
import PublicClientPortal from './pages/PublicClientPortal';
import PortalEstimateDetail from './pages/PortalEstimateDetail';
import PortalInvoiceDetail from './pages/PortalInvoiceDetail';
import PublicPayInvoice from './pages/PublicPayInvoice';
import PublicReviewThanks from './pages/PublicReviewThanks';
import PriceBook from './pages/PriceBook';
import Inventory from './pages/Inventory';
import Leads from './pages/Leads';
import EstimateTemplates from './pages/EstimateTemplates';
import MessageTemplates from './pages/MessageTemplates';
import Automations from './pages/Automations';
import ServicePlans from './pages/ServicePlans';
import ErrorBoundary from './components/ErrorBoundary';
import AppFooter, {
  APP_FOOTER_RESERVE_PX,
  DRAWER_COLLAPSED,
  DRAWER_EXPANDED,
} from './components/AppFooter';
import { isDriverPortalOnlyRole } from './constants/roles';
import { API_BASE_URL, ROUTER_BASENAME } from './api';
import { prefetchJobsList } from './utils/jobsListCache';
import { NotificationsProvider } from './context/NotificationsContext';
import GlobalKeyboardShortcuts from './components/GlobalKeyboardShortcuts';

/** Подновява JWT на всеки ~14 мин, докато има сесия (намалява изненадващ logout). */
function PeriodicJwtRefresh() {
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (!token || !user) {
      return undefined;
    }
    const intervalMs = 14 * 60 * 1000;
    const tick = async () => {
      const t = localStorage.getItem('token');
      if (!t) return;
      try {
        const res = await fetch(`${API_BASE_URL}auth/refresh`, {
          method: 'POST',
          headers: { Accept: 'application/json', Authorization: `Bearer ${t}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.token) {
          localStorage.setItem('token', data.token);
        }
      } catch {
        /* ignore network blips */
      }
    };
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, []);
  return null;
}

/** Preload jobs list after login so Jobs page is instant (incl. mobile PWA). */
function JobsListWarmup() {
  useEffect(() => {
    if (!localStorage.getItem('token')) return undefined;
    void prefetchJobsList();
    return undefined;
  }, []);
  return null;
}

/** Синхронизира UI настройки от акаунта след login / навигация. */
function SyncUserUiPreferences({ applyServerUiPrefs }) {
  const location = useLocation();
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}user`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const p = data.user?.ui_preferences;
        if (!p || typeof p !== 'object') return;
        applyServerUiPrefs(p);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, applyServerUiPrefs]);
  return null;
}

// Protected Route component with role-based access
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    setLoading(false);
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const roleName = (user && typeof user.role === 'object' && user.role && user.role.name) || user.role;

  if (allowedRoles.length > 0 && !allowedRoles.includes(roleName)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// Role-based route component
const RoleBasedRoute = ({ element, allowedRoles = [] }) => {
  return (
    <ProtectedRoute allowedRoles={allowedRoles}>
      {element}
    </ProtectedRoute>
  );
};

function AuthenticatedLayout({
  sidebarOpen,
  setSidebarOpen,
  mobileNavOpen,
  setMobileNavOpen,
  isMobile,
  darkMode,
  updateSettings,
  locale,
  setLocale,
  tableDensity,
  setTableDensity,
  timezone,
  setTimezone,
}) {
  const location = useLocation();
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      if (isDriverPortalOnlyRole(u.role) && location.pathname !== '/drivers/portal') {
        return <Navigate to="/drivers/portal" replace />;
      }
    }
  } catch {
    /* ignore */
  }

  let footerInset = 0;
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      if (!isDriverPortalOnlyRole(u.role)) {
        footerInset = sidebarOpen ? DRAWER_EXPANDED : DRAWER_COLLAPSED;
      }
    } else {
      footerInset = sidebarOpen ? DRAWER_EXPANDED : DRAWER_COLLAPSED;
    }
  } catch {
    footerInset = sidebarOpen ? DRAWER_EXPANDED : DRAWER_COLLAPSED;
  }

  // On mobile, the sidebar is temporary (overlay), so the footer must not be inset.
  if (isMobile) footerInset = 0;

  const isDriverOnlySession = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      return isDriverPortalOnlyRole(JSON.parse(raw).role);
    } catch {
      return false;
    }
  })();

  const showSidebar = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (isDriverPortalOnlyRole(user.role)) {
          return null;
        }
        return (
          <div data-testid="sidebar-container">
            <Sidebar
              open={sidebarOpen}
              onToggle={() => setSidebarOpen(!sidebarOpen)}
              variant={isMobile ? 'temporary' : 'permanent'}
              mobileOpen={mobileNavOpen}
              onMobileClose={() => setMobileNavOpen(false)}
              onToggleTheme={() => updateSettings(!darkMode)}
            />
          </div>
        );
      } catch {
        return (
          <Sidebar
            open={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            variant={isMobile ? 'temporary' : 'permanent'}
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
            onToggleTheme={() => updateSettings(!darkMode)}
          />
        );
      }
    }
    return (
      <Sidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        variant={isMobile ? 'temporary' : 'permanent'}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
        onToggleTheme={() => updateSettings(!darkMode)}
      />
    );
  };

  const mainSx = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (isDriverPortalOnlyRole(user.role)) {
          return { marginLeft: 0, width: '100%' };
        }
      } catch {
        /* fall through */
      }
    }
    if (isMobile) return { marginLeft: 0, width: '100%' };
    return {};
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          width: '100%',
          backgroundColor: 'background.default',
          paddingBottom: `${APP_FOOTER_RESERVE_PX}px`,
          boxSizing: 'border-box',
        }}
      >
        {showSidebar()}
        <Box
          component="main"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            padding: { xs: '12px', sm: '16px', md: '20px', lg: '24px' },
            paddingBottom: { xs: '12px', sm: '16px' },
            backgroundColor: 'transparent',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            ...mainSx(),
          }}
        >
          {isMobile && !isDriverOnlySession && (
            <Box
              sx={{
                width: '100%',
                maxWidth: '1280px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                py: 1,
                backgroundColor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <IconButton aria-label="Open menu" onClick={() => setMobileNavOpen(true)} size="large">
                <MenuIcon />
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }}>
                WorkWiseSoft
              </Typography>
            </Box>
          )}
          <Box sx={{ width: '100%', maxWidth: '1280px', minWidth: 0, flex: '1 1 auto', pb: 0 }}>
            <Routes>
              <Route
                index
                element={
                  <RoleBasedRoute
                    element={<Dashboard />}
                    allowedRoles={['super_admin', 'admin', 'office_admin', 'manager', 'user']}
                  />
                }
              />
              <Route path="clients" element={<RoleBasedRoute element={<Clients />} allowedRoles={['super_admin', 'admin', 'office_admin']} />} />
              <Route
                path="leads"
                element={
                  <RoleBasedRoute
                    element={<Leads />}
                    allowedRoles={['super_admin', 'admin', 'office_admin', 'manager']}
                  />
                }
              />
              <Route path="collections" element={<RoleBasedRoute element={<Collections />} allowedRoles={['super_admin', 'admin', 'office_admin', 'manager', 'user', 'driver', 'driver_novice', 'driver_pro']} />} />
              <Route path="jobs" element={<RoleBasedRoute element={<Jobs />} allowedRoles={['super_admin', 'admin', 'manager', 'office_admin']} />} />
              <Route path="fleet" element={<RoleBasedRoute element={<Drivers />} allowedRoles={['super_admin', 'admin', 'office_admin']} />} />
              <Route path="driver/:id" element={<RoleBasedRoute element={<DriverDetail />} allowedRoles={['super_admin', 'admin']} />} />
              <Route
                path="estimates"
                element={
                  <RoleBasedRoute
                    element={<Estimates />}
                    allowedRoles={['super_admin', 'admin', 'office_admin', 'manager', 'user']}
                  />
                }
              />
              <Route
                path="price-book"
                element={
                  <RoleBasedRoute
                    element={<PriceBook />}
                    allowedRoles={['super_admin', 'admin', 'office_admin', 'manager', 'user']}
                  />
                }
              />
              <Route
                path="inventory"
                element={
                  <RoleBasedRoute
                    element={<Inventory />}
                    allowedRoles={['super_admin', 'admin', 'office_admin', 'manager']}
                  />
                }
              />
              <Route
                path="estimate-templates"
                element={
                  <RoleBasedRoute
                    element={<EstimateTemplates />}
                    allowedRoles={['super_admin', 'admin', 'office_admin', 'manager', 'user']}
                  />
                }
              />
              <Route
                path="message-templates"
                element={
                  <RoleBasedRoute
                    element={<MessageTemplates />}
                    allowedRoles={['super_admin', 'admin', 'office_admin', 'manager', 'user']}
                  />
                }
              />
              <Route
                path="automations"
                element={
                  <RoleBasedRoute element={<Automations />} allowedRoles={['super_admin', 'admin']} />
                }
              />
              <Route
                path="service-plans"
                element={
                  <RoleBasedRoute
                    element={<ServicePlans />}
                    allowedRoles={['super_admin', 'admin', 'office_admin']}
                  />
                }
              />
              <Route path="expenses" element={<RoleBasedRoute element={<Expenses />} allowedRoles={['super_admin', 'admin']} />} />
              <Route path="invoices" element={<RoleBasedRoute element={<Invoices />} allowedRoles={['super_admin', 'admin', 'office_admin']} />} />
              <Route path="reports" element={<RoleBasedRoute element={<Reports />} allowedRoles={['super_admin', 'admin']} />} />
              <Route path="calendar" element={<RoleBasedRoute element={<Calendar />} allowedRoles={['super_admin', 'admin', 'office_admin']} />} />
              <Route path="users" element={<RoleBasedRoute element={<Users />} allowedRoles={['super_admin', 'admin']} />} />
              <Route
                path="settings"
                element={
                  <RoleBasedRoute
                    element={
                      <Settings
                        darkMode={darkMode}
                        updateSettings={updateSettings}
                        locale={locale}
                        setLocale={setLocale}
                        tableDensity={tableDensity}
                        setTableDensity={setTableDensity}
                        timezone={timezone}
                        setTimezone={setTimezone}
                      />
                    }
                    allowedRoles={['super_admin', 'admin', 'manager', 'user', 'office_admin']}
                  />
                }
              />
              <Route path="audit-log" element={<RoleBasedRoute element={<AuditLog />} allowedRoles={['super_admin', 'admin']} />} />
              <Route
                path="communication-log"
                element={<RoleBasedRoute element={<CommunicationLog />} allowedRoles={['super_admin', 'admin', 'office_admin']} />}
              />
              <Route
                path="google-ads-exports"
                element={<RoleBasedRoute element={<GoogleAdsExports />} allowedRoles={['super_admin', 'admin']} />}
              />
              <Route path="drivers/portal" element={<DriverPortal />} />
              <Route path="unauthorized" element={
                <div style={{ padding: 20 }}>
                  <h1>Access Denied</h1>
                  <p>You don&apos;t have permission to view this page.</p>
                  <div style={{ marginTop: 20, padding: 10, background: '#f0f0f0', borderRadius: 4 }}>
                    <p><strong>Debug Info:</strong></p>
                    <p>Current User Role: {JSON.parse(localStorage.getItem('user') || '{}').role || 'undefined'}</p>
                    <p>User Name: {JSON.parse(localStorage.getItem('user') || '{}').name || 'undefined'}</p>
                  </div>
                </div>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Box>
        </Box>
      </Box>
      <AppFooter insetLeft={footerInset} />
    </>
  );
}

// Theme will be created dynamically in the App component

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [locale, setLocale] = useState(() => {
    const stored = localStorage.getItem('appLocale');
    if (stored === 'bg') {
      localStorage.setItem('appLocale', 'en');
    }
    return 'en';
  });
  const [tableDensity, setTableDensity] = useState(() => localStorage.getItem('tableDensity') || 'comfortable');
  const [timezone, setTimezone] = useState(
    () => localStorage.getItem('appTimezone') || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [online, setOnline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine);

  useEffect(() => {
    setDarkMode(localStorage.getItem('darkMode') === 'true');
  }, []);

  useEffect(() => {
    dayjs.locale('en');
  }, [locale]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const applyServerUiPrefs = useCallback((p) => {
    if (typeof p.darkMode === 'boolean') {
      setDarkMode(p.darkMode);
      localStorage.setItem('darkMode', p.darkMode ? 'true' : 'false');
    }
    if (p.locale === 'en' || p.locale === 'bg') {
      setLocale('en');
      localStorage.setItem('appLocale', 'en');
      dayjs.locale('en');
    }
    if (p.tableDensity === 'comfortable' || p.tableDensity === 'compact') {
      setTableDensity(p.tableDensity);
      localStorage.setItem('tableDensity', p.tableDensity);
    }
    if (typeof p.timezone === 'string' && p.timezone.trim() !== '') {
      setTimezone(p.timezone);
      localStorage.setItem('appTimezone', p.timezone);
    }
  }, []);

  const muiLocale = enUS;
  const cellPad = tableDensity === 'compact' ? '6px 10px' : '16px';

  // Create dynamic theme based on settings
  const theme = useMemo(
    () =>
      createTheme(
        {
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: '#4F46E5', // Indigo-600
        light: '#818CF8', // Indigo-400
        dark: '#3730A3', // Indigo-800
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#8B5CF6', // Violet-500
        light: '#A78BFA',
        dark: '#5B21B6',
        contrastText: '#ffffff',
      },
      error: { main: '#EF4444' },
      warning: { main: '#F59E0B' },
      info: { main: '#3B82F6' },
      success: { main: '#10B981' },
      background: {
        default: darkMode ? '#0f1419' : '#f4f5f7',
        paper: darkMode ? '#1a222c' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#F8FAFC' : '#0F172A',
        secondary: darkMode ? '#94A3B8' : '#475569',
      },
      divider: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontFamily: '"Inter", sans-serif', fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.75rem' },
      h2: { fontFamily: '"Inter", sans-serif', fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.5rem' },
      h3: { fontFamily: '"Inter", sans-serif', fontWeight: 600, fontSize: '1.25rem' },
      h4: { fontFamily: '"Inter", sans-serif', fontWeight: 600, fontSize: '1.125rem' },
      h5: { fontFamily: '"Inter", sans-serif', fontWeight: 600, fontSize: '1rem' },
      h6: { fontFamily: '"Inter", sans-serif', fontWeight: 600, fontSize: '0.9375rem' },
      subtitle1: { fontWeight: 500, fontSize: '0.9375rem' },
      subtitle2: { fontWeight: 600, fontSize: '0.8125rem' },
      body2: { fontSize: '0.8125rem' },
      button: { fontFamily: '"Inter", sans-serif', fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em', fontSize: '0.8125rem' },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: 'all 0.3s ease',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: darkMode
              ? '0 1px 2px rgba(0, 0, 0, 0.35)'
              : '0 1px 2px rgba(15, 23, 42, 0.06)',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.08)',
          },
          elevation1: {
            boxShadow: darkMode
              ? '0 1px 2px rgba(0, 0, 0, 0.35)'
              : '0 1px 2px rgba(15, 23, 42, 0.06)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            marginBottom: '16px',
            borderRadius: 8,
            backgroundColor: darkMode ? '#1a222c' : '#ffffff',
            backgroundImage: 'none',
            boxShadow: darkMode
              ? '0 1px 2px rgba(0,0,0,0.35)'
              : '0 1px 2px rgba(15,23,42,0.06)',
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
            transition: 'box-shadow 0.15s ease',
            overflow: 'hidden',
            position: 'relative',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '6px 14px',
            boxShadow: 'none',
            fontSize: '0.8125rem',
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          containedPrimary: {
            background: '#4F46E5',
            color: '#ffffff',
            border: '1px solid #4338ca',
            '&:hover': {
              background: '#4338CA',
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            transition: 'background-color 0.15s ease',
            margin: '2px 8px',
            padding: '8px 12px',
            '&:hover': {
              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(79, 70, 229, 0.06)',
            },
            '&.Mui-selected': {
              backgroundColor: darkMode ? 'rgba(79, 70, 229, 0.22)' : 'rgba(79, 70, 229, 0.1)',
              color: darkMode ? '#a5b4fc' : '#4338CA',
              borderLeft: '3px solid #4F46E5',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(79, 70, 229, 0.28)' : 'rgba(79, 70, 229, 0.14)',
              },
              '& .MuiListItemIcon-root': {
                color: darkMode ? '#a5b4fc' : '#4F46E5',
              }
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: cellPad,
          },
        },
      },
    },
    spacing: 8,
        },
        muiLocale
      ),
    [darkMode, cellPad, muiLocale]
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /** Dark/light theme only. Persists to localStorage; Settings → Save syncs to server. */
  const updateSettings = useCallback((newDarkMode) => {
    setDarkMode(!!newDarkMode);
    localStorage.setItem('darkMode', newDarkMode ? 'true' : 'false');
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
        <NotificationsProvider>
          <ErrorBoundary>
            {!online && (
              <Alert severity="warning" sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, borderRadius: 0 }}>
                You are offline. Changes may fail until the connection is restored.
              </Alert>
            )}
            <Router basename={ROUTER_BASENAME}>
              <>
                <GlobalKeyboardShortcuts />
                <PeriodicJwtRefresh />
                <JobsListWarmup />
                <SyncUserUiPreferences applyServerUiPrefs={applyServerUiPrefs} />
                <Routes>
            {/* Public routes (without sidebar) */}
            <Route path="/login" element={<Login />} />
            <Route path="/drivers" element={<Login variant="driver" />} />
            <Route path="/driver-login" element={<Navigate to="/drivers" replace />} />
            <Route path="/driver-portal" element={<Navigate to="/drivers/portal" replace />} />
            <Route path="/register" element={<Register />} />
            <Route path="/public/estimate/:token" element={<PublicEstimate />} />
            <Route path="/public/booking" element={<PublicBooking />} />
            <Route path="/public/booking/embed" element={<PublicBooking embedMode />} />
            <Route path="/public/client/:token" element={<PublicClientPortal />} />
            <Route path="/public/client/:token/estimate/:estimateId" element={<PortalEstimateDetail />} />
            <Route path="/public/client/:token/invoice/:invoiceId" element={<PortalInvoiceDetail />} />
            <Route path="/public/pay/invoice/:token" element={<PublicPayInvoice />} />
            <Route path="/public/review/thanks" element={<PublicReviewThanks />} />

            {/* Design Replica Route (Standalone Layout) */}
            <Route path="/sop-library" element={
              <ProtectedRoute>
                <SOPLibrary />
              </ProtectedRoute>
            } />

            {/* Protected routes (with sidebar) */}
            <Route path="/*" element={
              <ProtectedRoute>
                <AuthenticatedLayout
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                  mobileNavOpen={mobileNavOpen}
                  setMobileNavOpen={setMobileNavOpen}
                  isMobile={isMobile}
                  darkMode={darkMode}
                  updateSettings={updateSettings}
                  locale={locale}
                  setLocale={setLocale}
                  tableDensity={tableDensity}
                  setTableDensity={setTableDensity}
                  timezone={timezone}
                  setTimezone={setTimezone}
                />
              </ProtectedRoute>
            } />
                </Routes>
              </>
            </Router>
          </ErrorBoundary>
        </NotificationsProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
